import { DownOutlined, LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { Avatar, Dropdown, Space, Typography } from "antd";
import { useSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import NotificationPopover from "../../components/NotificationPopover";
import {
  setAllNotifications,
  setBackupTrialInfo,
  setUserData,
  setEmailVerified,
  setProfileCompleted,
  setIsBlocked,
  setDemoTourFinished,
} from "../../store/common";
import { readBackupNotification } from "../../utils/backupTrial";
import { getAllNotifications } from "../../utils/notifications";
import { checkAuthentication, getUser } from "../../utils/users";
import { consumePostOnboardingProfileRedirect } from "../../utils/postOnboardingRedirect";
import { isIntuitConnection, isIntuitUnverified } from "../../utils/auth";
import SuccessModal from "../../modals/SuccessModal";
import {useContextState} from "../../context/ContextProvider";


const { Title, Text } = Typography;

const Header = () => {
  const { logout, getIdTokenClaims, user } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { setRole } = useContextState();
  const searchParams = new URLSearchParams(location.search);

  const [accountClosingModalOpen, setAccountClosingModalOpen] = useState(false);

  const dispatch = useDispatch();
  const userData = useSelector((state) => state.commonReducer.userData);

  // Simple function to get page title based on current route
  const getPageTitle = () => {
    // Get the main path segment (first part after /)
    const path = location.pathname.split("/")[1] || "dashboard";

    // Special handling for archive routes

    // Simple mapping for other routes
    const titles = {
      "": "Dashboard",
      archive: "Archived Organizations",
      users: "Users",
      organizations: "Organizations",
      "audit-log": "Audit Log",
      "payment-methods": "Payment Methods",
      settings: "Settings",
    };

    // Return the mapped title or default to Dashboard
    return titles[path] || "Dashboard";
  };

  // Fetch user data and notifications
  const fetchUserData = async () => {
    try {
      const tokenClaims = await getIdTokenClaims();
      
      // Check if token claims are available
      if (!tokenClaims || !tokenClaims.__raw) {
        enqueueSnackbar("Authentication token not available. Please log in again.", {
          variant: "error",
        });
        logout({ logoutParams: { returnTo: window.location.origin } });
        return;
      }
      
      // Fast-path guard: check if current user is an unverified Intuit account
      if (isIntuitUnverified(tokenClaims, user)) {
        dispatch(setEmailVerified(false));
        navigate("/intuit-unverified", { replace: true });
        return;
      }

      const token = tokenClaims.__raw; // ✅ ID TOKEN (JWT)

      let authCheckResponse;
      try {
        authCheckResponse = await checkAuthentication(token);
      } catch (authErr) {
        if (
          authErr?.response?.data?.is_intuit_unverified ||
          authErr?.response?.data?.error === "INTUIT_EMAIL_NOT_VERIFIED"
        ) {
          dispatch(setEmailVerified(false));
          navigate("/intuit-unverified", { replace: true });
          return;
        }
        throw authErr;
      }

      if (!authCheckResponse?.data) {
        navigate("/login");
        return;
      }

      if (
        authCheckResponse.data.is_intuit_unverified ||
        authCheckResponse.data.error === "INTUIT_EMAIL_NOT_VERIFIED"
      ) {
        dispatch(setEmailVerified(false));
        navigate("/intuit-unverified", { replace: true });
        return;
      }

      const isIntuitUser = isIntuitConnection(tokenClaims?.sub || user?.sub);

      if (authCheckResponse.data.success) {
        const profileComplete =
          authCheckResponse.data.profile_completed !== false;
        const demoTourRaw =
          authCheckResponse.data.demo_tour_finished ??
          authCheckResponse.data.user?.demo_tour_finished;
        dispatch(setEmailVerified(true));
        dispatch(setProfileCompleted(profileComplete));
        dispatch(setIsBlocked(authCheckResponse.data.is_blocked || false));
        dispatch(
          setDemoTourFinished(
            demoTourRaw === 1 ||
              demoTourRaw === "1" ||
              demoTourRaw === true
          )
        );

        // Check if user is blocked
        if (authCheckResponse.data.is_blocked === true) {
          enqueueSnackbar("Your account has been blocked. Please contact support for assistance.", {
            variant: "error",
          });
          logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          });
          return;
        }

        if (!profileComplete && !isIntuitUser) {
          const claims = await getIdTokenClaims();
          const params = new URLSearchParams();
          const profileEmail =
            claims?.email || authCheckResponse.data.user?.email || "";
          const profileSub = claims?.sub || "";
          if (profileEmail) params.set("email", profileEmail);
          if (profileSub) params.set("sub", profileSub);
          const qs = params.toString();
          navigate(qs ? `/complete-profile?${qs}` : "/complete-profile", {
            replace: true,
          });
          return;
        }

        if (consumePostOnboardingProfileRedirect()) {
          navigate("/settings/profile", { replace: true });
          return;
        }

        // Check if this is a new user and redirect to profile page
        if (authCheckResponse.data.new_user === true) {
          navigate("/settings/profile", { replace: true });
          return;
        }

        const [userRes, notificationsRes, backupNoti] = await Promise.all([
          getUser(token),
          getAllNotifications(token),
          readBackupNotification(token),
        ]);
        
        // Add null checks for API responses
        if (userRes?.data) {
          if (userRes.data.is_account_closed == 1) {
            setAccountClosingModalOpen(true);
          }
          if (authCheckResponse.data.user?.role) {
            setRole(authCheckResponse.data.user.role);
          }
          dispatch(setUserData(userRes.data));
        }
        
        if (backupNoti?.data?.trialInfo) {
          dispatch(setBackupTrialInfo(backupNoti.data.trialInfo));
        }
        
        if (notificationsRes?.data?.notifications) {
          dispatch(setAllNotifications(notificationsRes.data.notifications));
        }
      } else if (
        !authCheckResponse.data.success &&
        authCheckResponse.data.profile_completed === false
      ) {
        if (isIntuitUser) {
          dispatch(setEmailVerified(false));
          navigate("/intuit-unverified", { replace: true });
          return;
        }
        dispatch(setEmailVerified(false));
        dispatch(setProfileCompleted(false));
        const claims = await getIdTokenClaims();
        const params = new URLSearchParams();
        const profileEmail =
          claims?.email || authCheckResponse.data.user?.email || "";
        const profileSub = claims?.sub || "";
        if (profileEmail) params.set("email", profileEmail);
        if (profileSub) params.set("sub", profileSub);
        const qs = params.toString();
        navigate(qs ? `/complete-profile?${qs}` : "/complete-profile", { replace: true });
        return;
      } else {
        // Other errors - logout
        navigate("/login");
      }
    } catch (error) {
      // Add proper error handling with null checks
      console.error("Error fetching user data:", error);
      
      // Handle token retrieval errors specifically
      if (error?.error === "login_required" || error?.message?.includes("token")) {
        enqueueSnackbar("Session expired. Please log in again.", {
          variant: "error",
        });
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
        return;
      }
      
      // Handle 500 errors (server errors) differently from auth errors
      if (error?.response?.status === 500) {
        // Server error - show a more user-friendly message
        enqueueSnackbar("Server is temporarily unavailable. Please try again in a moment.", {
          variant: "error",
        });
        return; // Don't logout on server errors
      }
      
      // Handle 401/403 errors (authentication failures)
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        const errorMessage = error?.response?.data?.message || 
                            "Authentication failed. Please log in again.";
        enqueueSnackbar(errorMessage, {
          variant: "error",
        });
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
        return;
      }
      
      // Other errors
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          "An error occurred while fetching user data";
      
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    // logout({ returnTo: window.location.origin });
  };

  // Profile dropdown menu items
  const dropdownItems = [
    {
      key: "profile",
      label: (
        <Link to="/settings/profile" className="flex items-center">
          <UserOutlined style={{ marginRight: 8 }} />
          Profile
        </Link>
      ),
    },
    {
      key: "logout",
      label: (
        <div className="flex items-center text-red-500" onClick={handleLogout}>
          <LogoutOutlined style={{ marginRight: 8 }} />
          Logout
        </div>
      ),
    },
  ];

  return (
    <header className="w-full py-2 px-5 border-b border-gray-200 bg-white">
      <div className="flex justify-between items-center">
        <Title level={3} style={{ margin: 0 }}>
          {getPageTitle()}
        </Title>

        <div className="flex items-center gap-6">
          {/* Notification Bell Component */}
          <div className="app-tour-notifications">
            <NotificationPopover />
          </div>

          {/* Profile Dropdown */}
          <Dropdown
            menu={{ items: dropdownItems }}
            placement="bottomRight"
            arrow
            trigger={["click"]}
          >
            <Space className="app-tour-profile cursor-pointer">
              <Avatar
                size="large"
                src={userData.picture}
                icon={!userData.picture && <UserOutlined />}
              />
              <div className="hidden md:block">
                <Text strong>{userData.name}</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: "0.875rem" }}>
                    {userData.email}
                  </Text>
                </div>
              </div>
              <DownOutlined style={{ fontSize: "12px" }} />
            </Space>
          </Dropdown>

          <SuccessModal
            title="Account was closed"
            text="Your account was closed as per the submitted request. Contact with the support if you need any assistances."
            modalOpen = {accountClosingModalOpen}
            onModalClose={() => {
              logout({
                logoutParams: {
                  returnTo: window.location.origin,
                },
              });
            }}
          />
        </div>
      </div>
    </header>
  );
};

export default Header;
